/**
 * Semver utilities following https://semver.org/ specification.
 *
 * Supports:
 * - Standard format: MAJOR.MINOR.PATCH (e.g., "1.0.0")
 * - Pre-release: MAJOR.MINOR.PATCH-prerelease (e.g., "1.0.0-alpha", "1.0.0-alpha.1")
 * - Build metadata: MAJOR.MINOR.PATCH+build (e.g., "1.0.0+build123") - ignored for precedence
 * - Optional 'V' or 'v' prefix (e.g., "V1.0.0", "v1.0.0")
 */
/**
 * Parses a semver version string into its components.
 * Handles optional 'V'/'v' prefix and ignores build metadata.
 * Returns null if the version string is invalid.
 */
function parseSemver(version) {
    if (!version) {
        return null;
    }
    // Remove optional 'V' or 'v' prefix
    var cleanVersion = version.replace(/^[Vv]/, '');
    // Remove build metadata (everything after '+') - not used for precedence
    var buildIndex = cleanVersion.indexOf('+');
    if (buildIndex !== -1) {
        cleanVersion = cleanVersion.substring(0, buildIndex);
    }
    // Split off pre-release identifier (everything after '-')
    var prerelease = [];
    var prereleaseIndex = cleanVersion.indexOf('-');
    if (prereleaseIndex !== -1) {
        var prereleaseStr = cleanVersion.substring(prereleaseIndex + 1);
        cleanVersion = cleanVersion.substring(0, prereleaseIndex);
        prerelease = prereleaseStr.split('.');
    }
    // Parse MAJOR.MINOR.PATCH
    var parts = cleanVersion.split('.');
    if (parts.length !== 3) {
        return null;
    }
    var major = parseInt(parts[0], 10);
    var minor = parseInt(parts[1], 10);
    var patch = parseInt(parts[2], 10);
    if (isNaN(major) || isNaN(minor) || isNaN(patch)) {
        return null;
    }
    if (major < 0 || minor < 0 || patch < 0) {
        return null;
    }
    return { major: major, minor: minor, patch: patch, prerelease: prerelease };
}
/**
 * Compares two pre-release identifier arrays according to semver precedence rules.
 *
 * Rules from semver.org:
 * 1. Identifiers are compared left to right
 * 2. Numeric identifiers are compared as integers
 * 3. Alphanumeric identifiers are compared lexically (ASCII sort)
 * 4. Numeric identifiers always have lower precedence than alphanumeric
 * 5. A larger set of pre-release fields has higher precedence if all preceding are equal
 *
 * Returns negative if a < b, 0 if equal, positive if a > b
 */
function comparePrerelease(a, b) {
    // No pre-release has HIGHER precedence than any pre-release
    // e.g., 1.0.0 > 1.0.0-alpha
    if (a.length === 0 && b.length === 0) {
        return 0;
    }
    if (a.length === 0) {
        return 1; // a is release, b is pre-release: a > b
    }
    if (b.length === 0) {
        return -1; // a is pre-release, b is release: a < b
    }
    // Compare identifiers left to right
    var maxLength = Math.max(a.length, b.length);
    for (var i = 0; i < maxLength; i++) {
        // Larger set has higher precedence if all preceding are equal
        if (i >= a.length) {
            return -1; // a has fewer identifiers: a < b
        }
        if (i >= b.length) {
            return 1; // b has fewer identifiers: a > b
        }
        var aId = a[i];
        var bId = b[i];
        var aIsNumeric = /^\d+$/.test(aId);
        var bIsNumeric = /^\d+$/.test(bId);
        if (aIsNumeric && bIsNumeric) {
            // Both numeric: compare as integers
            var diff = parseInt(aId, 10) - parseInt(bId, 10);
            if (diff !== 0) {
                return diff;
            }
        }
        else if (aIsNumeric) {
            // Numeric has lower precedence than alphanumeric
            return -1;
        }
        else if (bIsNumeric) {
            // Numeric has lower precedence than alphanumeric
            return 1;
        }
        else {
            // Both alphanumeric: compare lexically
            var cmp = aId.localeCompare(bId);
            if (cmp !== 0) {
                return cmp;
            }
        }
    }
    return 0;
}
/**
 * Compares two semver versions according to semver.org precedence rules.
 *
 * Returns:
 *   - negative number if a < b
 *   - 0 if a === b
 *   - positive number if a > b
 *   - null if either version is invalid
 *
 * Examples:
 *   compareSemver("1.0.0", "2.0.0") < 0
 *   compareSemver("1.0.0-alpha", "1.0.0") < 0
 *   compareSemver("1.0.0-alpha", "1.0.0-beta") < 0
 *   compareSemver("1.0.0-alpha.1", "1.0.0-alpha.2") < 0
 */
export function compareSemver(a, b) {
    var parsedA = parseSemver(a);
    var parsedB = parseSemver(b);
    if (!parsedA || !parsedB) {
        return null;
    }
    // Compare MAJOR.MINOR.PATCH
    if (parsedA.major !== parsedB.major) {
        return parsedA.major - parsedB.major;
    }
    if (parsedA.minor !== parsedB.minor) {
        return parsedA.minor - parsedB.minor;
    }
    if (parsedA.patch !== parsedB.patch) {
        return parsedA.patch - parsedB.patch;
    }
    // Compare pre-release
    return comparePrerelease(parsedA.prerelease, parsedB.prerelease);
}
/**
 * Checks if a firmware version meets the minimum required version.
 * Both versions can optionally be prefixed with 'V' or 'v'.
 * Returns false if the firmware version is undefined/null or invalid.
 *
 * Examples:
 *   meetsMinimumVersion("V0.7.3", "0.7.3") === true
 *   meetsMinimumVersion("V0.7.2", "0.7.3") === false
 *   meetsMinimumVersion("V0.7.3-beta", "0.7.3") === false (pre-release < release)
 *   meetsMinimumVersion("V0.8.0", "0.7.3") === true
 */
export function meetsMinimumVersion(firmwareVersion, minimumVersion) {
    if (!firmwareVersion) {
        return false;
    }
    var comparison = compareSemver(firmwareVersion, minimumVersion);
    return comparison !== null && comparison >= 0;
}
